import React, { useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCallCenterOptional } from '../../contexts/CallCenterContext';
import { useSocket, useSocketEvent } from '../../hooks/useSocket';
import CallScreenPopModal from './CallScreenPopModal';

/**
 * گوش دادن به رویدادهای زنده مرکز تماس از طریق Socket.io (karmadws)
 * به محض ارسال وب‌هوک توسط استریسک، پنجره Screen Pop برای اپراتور مربوطه باز می‌شود.
 */
const CallScreenPopPoller = () => {
  const { user, isAuthenticated } = useAuth();
  const callCenter = useCallCenterOptional();
  const { emit } = useSocket();
  const shownIdsRef = useRef(new Set());
  const loadExtRef = useRef(false);

  const isActive = Boolean(isAuthenticated && callCenter?.isCallCenterActive);

  // لود داخلی اپراتور در صورت لاگین
  useEffect(() => {
    if (isActive && !loadExtRef.current && callCenter?.loadMyAgentExtension) {
      loadExtRef.current = true;
      callCenter.loadMyAgentExtension();
    }
  }, [isActive, callCenter]);

  // ثبت و پیوستن به اتاق‌های داخلی و شناسه اپراتور در سوکت
  useEffect(() => {
    if (!isActive) return;

    if (user?.id) {
      emit('join-agent-room', user.id);
    }

    const exts = (
      Array.isArray(callCenter?.myAgentExtensions) && callCenter.myAgentExtensions.length > 0
        ? callCenter.myAgentExtensions
        : [callCenter?.myAgentExtension]
    ).filter(Boolean);

    exts.forEach((ext) => {
      emit('join-extension', String(ext).trim());
    });
  }, [isActive, user?.id, callCenter?.myAgentExtension, callCenter?.myAgentExtensions, emit]);

  /**
   * بررسی دقیق اینکه آیا تماس مربوط به داخلی همین اپراتور است یا خیر.
   * اگر داخلی تماس مثلاً 212 باشد، فقط اپراتوری که داخلی 212 دارد پاپ‌آپ را دریافت می‌کند.
   */
  const isCallForCurrentAgent = (data) => {
    if (!data) return false;

    // ۱. بررسی جهت تماس: پاپ‌آپ تنها و تنها برای تماس‌های ورودی (incoming) است! تماس‌های خروجی پاپ‌آپ ندارند
    const direction = String(data.direction || data.call?.direction || '').toLowerCase().trim();
    if (direction && direction !== 'incoming' && direction !== 'inbound') {
      console.log(`[Socket] Suppressed popup for non-incoming call (direction: ${direction})`);
      return false;
    }

    const eventExt = String(data.extension || data.call?.extension || '').trim();
    const myExts = (
      Array.isArray(callCenter?.myAgentExtensions) && callCenter.myAgentExtensions.length > 0
        ? callCenter.myAgentExtensions
        : [callCenter?.myAgentExtension]
    )
      .filter(Boolean)
      .map((e) => String(e).trim());

    const currentUserId = user?.id ? Number(user.id) : null;
    const targetAgentIds = Array.isArray(data.agent_id)
      ? data.agent_id.map(Number)
      : (data.agent_id ? [Number(data.agent_id)] : []);

    if (data.call?.agent_user_id) {
      targetAgentIds.push(Number(data.call.agent_user_id));
    }

    // ۱. اگر داخلی تماس مشخص باشد (معیار اصلی)
    if (eventExt) {
      const isMyExtension = myExts.includes(eventExt);
      if (!isMyExtension) {
        // تماس مربوط به داخلی دیگری است؛ هیچ پاپ‌آپی برای این کاربر باز نمی‌شود
        return false;
      }
      return true;
    }

    // ۲. اگر داخلی در پیلود نبود اما شناسه کاربر اپراتور مشخص بود
    if (targetAgentIds.length > 0) {
      return Boolean(currentUserId && targetAgentIds.includes(currentUserId));
    }

    // ۳. اگر هیچ تطابقی وجود نداشت، نمایش پاپ‌آپ مسدود می‌شود
    return false;
  };

  // ۱. رویداد زنگ خوردن تلفن (ringing)
  useSocketEvent(
    'ringing',
    (data) => {
      if (!callCenter || !data) return;
      if (!isCallForCurrentAgent(data)) return;

      const callId = data.call_id || data.call?.id;
      if (callId && shownIdsRef.current.has(callId)) return;
      if (callId) shownIdsRef.current.add(callId);

      console.log('[Socket] Real-time ringing call received for this extension:', data);
      callCenter.openScreenPop({ data });
    },
    isActive,
  );

  // ۲. رویداد اسکرین‌پاپ اختصاصی (screen_pop)
  useSocketEvent(
    'screen_pop',
    (data) => {
      if (!callCenter || !data) return;
      if (!isCallForCurrentAgent(data)) return;

      const callId = data.call_id || data.call?.id;
      if (callId && shownIdsRef.current.has(callId)) return;
      if (callId) shownIdsRef.current.add(callId);

      console.log('[Socket] Real-time screen_pop received for this extension:', data);
      callCenter.openScreenPop({ data });
    },
    isActive,
  );

  // ۳. رویداد قطع تماس (hangup)
  useSocketEvent(
    'hangup',
    (data) => {
      if (!callCenter || !data) return;
      if (!isCallForCurrentAgent(data)) return;
      console.log('[Socket] Real-time hangup event received for this extension:', data);
    },
    isActive,
  );

  if (!callCenter) return null;

  return <CallScreenPopModal />;
};

export default CallScreenPopPoller;
